import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProject } from '../contexts/ProjectContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Building2, Users, CalendarDays, AlertTriangle, TrendingUp, Clock, CheckCircle2, Target, Loader2, ArrowRight, Zap, BarChart3 } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';

const DashboardPage = () => {
  const { user } = useAuth();
  const { projects, fetchProjects, activeProject, loadProject } = useProject();
  const navigate = useNavigate();
  
  const [activities, setActivities] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [gamificationMetrics, setGamificationMetrics] = useState(null);
  const [loadingGamification, setLoadingGamification] = useState(false);

  // Fetch projects on mount
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Fetch activities for active schedule
  useEffect(() => {
    const fetchActivities = async () => {
      if (!activeProject?.active_schedule_id) {
        setActivities([]);
        return;
      }
      
      setLoadingActivities(true);
      try {
        const response = await api.get(`/activities/schedule/${activeProject.active_schedule_id}`);
        setActivities(response.data);
      } catch (error) {
        console.error('Failed to fetch activities:', error);
        setActivities([]);
      } finally {
        setLoadingActivities(false);
      }
    };
    
    fetchActivities();
  }, [activeProject?.active_schedule_id]);

  // Fetch all users (for team count) - shown to all authenticated org users
  useEffect(() => {
    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        // Users endpoint might not exist or be restricted, so handle gracefully
        const response = await api.get('/admin/users');
        setUsers(response.data || []);
      } catch (error) {
        // Silently fail - users endpoint may be restricted
        setUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };

    // GET /admin/users is admin-only (MANAGE_USERS). Gating on 'member' too
    // fired the call for every persona -> two 403s per dashboard load. Only
    // the admin tiers can read it.
    if (['super_admin', 'admin'].includes(user?.role)) {
      fetchUsers();
    }
  }, [user?.role]);

  // Fetch gamification metrics - shown to all authenticated org users
  useEffect(() => {
    const fetchGamification = async () => {
      // Check if user can see gamification
      const allowedRoles = ['super_admin', 'admin', 'member'];
      if (!user?.role || !allowedRoles.includes(user.role)) {
        return;
      }
      
      setLoadingGamification(true);
      try {
        const params = activeProject?.id ? { project_id: activeProject.id } : {};
        const response = await api.get('/gamification/metrics', { params });
        setGamificationMetrics(response.data);
      } catch (error) {
        console.error('Failed to fetch gamification metrics:', error);
        setGamificationMetrics(null);
      } finally {
        setLoadingGamification(false);
      }
    };
    
    fetchGamification();
  }, [user?.role, activeProject?.id]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const formatRole = (role) => {
    if (!role) return '';
    return role.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  // Admins/super-admins see team/aggregated wording; members see "your" wording.
  const showsTeamWording = ['admin', 'super_admin'].includes(user?.role);

  // Calculate real stats from data
  const stats = useMemo(() => {
    // Active Projects = projects with status 'active'
    const activeProjects = projects.filter(p => p.status === 'active').length;
    
    // Pending Tasks = activities that are not_started or in_progress
    const pendingTasks = activities.filter(a => 
      a.status === 'not_started' || a.status === 'in_progress'
    ).length;
    
    // Team Members = total users (or 1 if we can't fetch)
    const teamMembers = users.length || 1;
    
    // On-Time Rate = activities completed / activities that should be complete (based on late_finish)
    let onTimeRate = '—';
    const completedActivities = activities.filter(a => a.status === 'completed');
    const today = new Date();
    
    if (completedActivities.length > 0) {
      const onTimeCount = completedActivities.filter(a => {
        if (!a.late_finish) return true; // No late finish = on time
        return new Date(a.actual_finish || a.early_finish) <= new Date(a.late_finish);
      }).length;
      onTimeRate = `${Math.round((onTimeCount / completedActivities.length) * 100)}%`;
    } else if (activities.length > 0) {
      // If no completed activities yet, show based on critical path status
      const criticalActivities = activities.filter(a => a.is_critical);
      const nonCriticalDelayed = activities.filter(a => 
        !a.is_critical && a.total_float !== null && a.total_float < 0
      ).length;
      
      if (criticalActivities.length > 0 || nonCriticalDelayed > 0) {
        const healthyActivities = activities.filter(a => 
          a.total_float === null || a.total_float >= 0
        ).length;
        onTimeRate = `${Math.round((healthyActivities / activities.length) * 100)}%`;
      } else {
        onTimeRate = '100%';
      }
    }
    
    return [
      { 
        title: 'Active Projects', 
        value: activeProjects.toString(), 
        icon: Building2, 
        change: null,
        action: () => navigate('/projects')
      },
      { 
        title: 'Pending Tasks', 
        value: pendingTasks.toString(), 
        icon: CalendarDays, 
        change: null,
        action: activeProject ? () => navigate('/scheduling') : null
      },
      { 
        title: 'Team Members', 
        value: teamMembers.toString(), 
        icon: Users, 
        change: null,
        action: null
      },
      { 
        title: 'On-Time Rate', 
        value: onTimeRate, 
        icon: TrendingUp, 
        change: null,
        action: null
      },
    ];
  }, [projects, activities, users, activeProject, navigate]);

  // Calculate schedule health metrics
  const scheduleHealth = useMemo(() => {
    if (activities.length === 0) return null;
    
    const criticalCount = activities.filter(a => a.is_critical).length;
    const completedCount = activities.filter(a => a.status === 'completed').length;
    const inProgressCount = activities.filter(a => a.status === 'in_progress').length;
    const notStartedCount = activities.filter(a => a.status === 'not_started').length;
    
    // Calculate average float (for non-critical activities)
    const nonCriticalWithFloat = activities.filter(a => !a.is_critical && a.total_float !== null);
    const avgFloat = nonCriticalWithFloat.length > 0 
      ? Math.round(nonCriticalWithFloat.reduce((sum, a) => sum + a.total_float, 0) / nonCriticalWithFloat.length)
      : 0;
    
    return {
      criticalCount,
      completedCount,
      inProgressCount,
      notStartedCount,
      avgFloat,
      total: activities.length,
      completionPercent: Math.round((completedCount / activities.length) * 100)
    };
  }, [activities]);

  // Get recent activities (latest 5 by updated_at)
  const recentActivities = useMemo(() => {
    return [...activities]
      .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
      .slice(0, 5);
  }, [activities]);

  // Get upcoming milestones (activities with 0 duration or marked as milestone-like)
  const upcomingMilestones = useMemo(() => {
    return activities
      .filter(a => a.status !== 'completed' && a.early_finish)
      .sort((a, b) => new Date(a.early_finish) - new Date(b.early_finish))
      .slice(0, 3);
  }, [activities]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const handleProjectClick = async (projectId) => {
    await loadProject(projectId);
    navigate('/scheduling');
  };

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Welcome Section */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900" data-testid="welcome-heading">
            {getGreeting()}, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {formatRole(user?.role)} • Epic Plan
          </p>
        </div>
        {activeProject && (
          <Badge variant="secondary" className="text-xs max-w-full min-w-0 shrink-0">
            <span className="truncate">{activeProject.name}</span>
          </Badge>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stats-grid">
        {stats.map((stat) => (
          <Card 
            key={stat.title} 
            className={`border-slate-200 ${stat.action ? 'cursor-pointer hover:border-slate-300 transition-colors' : ''}`}
            onClick={stat.action}
          >
            <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
              <CardTitle className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                {stat.title}
              </CardTitle>
              <stat.icon className="h-4 w-4 text-slate-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold text-slate-900 tabular-nums">
                {stat.value}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Gamification Cards - Visible to all authenticated org users */}
      {gamificationMetrics && !loadingGamification && ['super_admin', 'admin', 'member'].includes(user?.role) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="gamification-cards">
          {/* Productivity Progress Card */}
          <Card className="border-slate-200 bg-gradient-to-br from-slate-50 to-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-700 flex items-center gap-2">
                <Zap className="h-4 w-4 text-amber-500" />
                Productivity Progress
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-slate-600">
                {showsTeamWording && gamificationMetrics.productivity.superintendent_count !== null ? (
                  <>
                    You have completed{' '}
                    <span className="font-semibold text-slate-900">
                      {gamificationMetrics.productivity.display_updates}
                    </span>
                    {' '}{gamificationMetrics.productivity.metric_label || 'updates'}{' '}
                    across{' '}
                    <span className="font-semibold text-slate-900">
                      {gamificationMetrics.productivity.superintendent_count}
                    </span>
                    {' '}field staff this month
                  </>
                ) : (
                  <>
                    You have completed{' '}
                    <span className="font-semibold text-slate-900">
                      {gamificationMetrics.productivity.display_updates}
                    </span>
                    {' / '}
                    <span className="font-semibold text-slate-900">
                      {gamificationMetrics.productivity.monthly_target}
                    </span>
                    {' '}{gamificationMetrics.productivity.metric_label || 'updates'}{' '}this month
                  </>
                )}
              </p>
              <div className="space-y-1">
                <div className="flex justify-between text-xs text-slate-500">
                  <span>
                    {showsTeamWording
                      ? 'Overall Schedule Update Completion'
                      : 'Monthly Progress'}
                  </span>
                  <span className="font-medium">{gamificationMetrics.productivity.percentage}%</span>
                </div>
                <Progress 
                  value={gamificationMetrics.productivity.percentage} 
                  className="h-2"
                />
              </div>
              {gamificationMetrics.productivity.exceeded_target && (
                <p className="text-xs text-emerald-600 font-medium flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  {showsTeamWording
                    ? 'Your team exceeded the monthly target 🎉'
                    : 'You exceeded your monthly target 🎉'}
                </p>
              )}
              {gamificationMetrics.role_context && (
                <p className="text-[10px] text-slate-400 italic">
                  {gamificationMetrics.role_context}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Performance Comparison Card */}
          <Card className="border-slate-200 bg-gradient-to-br from-slate-50 to-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-700 flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-blue-500" />
                Performance Comparison
              </CardTitle>
            </CardHeader>
            <CardContent>
              {gamificationMetrics.performance ? (
                <div className="space-y-2">
                  <p className="text-sm text-slate-600">
                    {gamificationMetrics.performance.comparison_text}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <div className={`text-2xl font-bold ${
                      gamificationMetrics.performance.is_higher 
                        ? 'text-emerald-600' 
                        : 'text-slate-600'
                    }`}>
                      {gamificationMetrics.performance.is_higher ? '+' : '-'}
                      {gamificationMetrics.performance.percentage_difference}%
                    </div>
                    <div className="text-xs text-slate-500">
                      <p>{showsTeamWording ? 'Team rate' : 'Your rate'}: {gamificationMetrics.performance.production_rate}%</p>
                      <p>Org avg: {gamificationMetrics.performance.comparison_rate}%</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-sm text-slate-500">
                    {activeProject 
                      ? 'Performance data will appear when other projects have activity'
                      : 'Select a project to see performance comparison'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Schedule Health Overview - 2 columns */}
        <Card className="border-slate-200 lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <Target className="h-4 w-4 text-slate-400" />
              Schedule Health Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingActivities ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : scheduleHealth ? (
              <div className="space-y-4">
                {/* Progress Bar */}
                <div>
                  <div className="flex justify-between text-xs text-slate-500 mb-1">
                    <span>Overall Completion</span>
                    <span className="font-medium">{scheduleHealth.completionPercent}%</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-emerald-500 rounded-full transition-all"
                      style={{ width: `${scheduleHealth.completionPercent}%` }}
                    />
                  </div>
                </div>
                
                {/* Stats Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-2">
                  <div className="text-center p-3 bg-slate-50 rounded-lg">
                    <p className="text-lg font-semibold text-slate-900">{scheduleHealth.total}</p>
                    <p className="text-[10px] text-slate-500 uppercase">Total Activities</p>
                  </div>
                  <div className="text-center p-3 bg-amber-50 rounded-lg">
                    <p className="text-lg font-semibold text-amber-600">{scheduleHealth.criticalCount}</p>
                    <p className="text-[10px] text-amber-600 uppercase">Critical Path</p>
                  </div>
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <p className="text-lg font-semibold text-blue-600">{scheduleHealth.inProgressCount}</p>
                    <p className="text-[10px] text-blue-500 uppercase">In Progress</p>
                  </div>
                  <div className="text-center p-3 bg-emerald-50 rounded-lg">
                    <p className="text-lg font-semibold text-emerald-600">{scheduleHealth.completedCount}</p>
                    <p className="text-[10px] text-emerald-500 uppercase">Completed</p>
                  </div>
                </div>

                {/* Average Float */}
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="text-sm text-slate-600">Average Float</span>
                  <span className="text-sm font-medium text-slate-900">{scheduleHealth.avgFloat} days</span>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                  <TrendingUp className="h-8 w-8 text-slate-300" />
                </div>
                <p className="text-sm font-medium text-slate-900 mb-1">No schedule data</p>
                <p className="text-xs text-slate-400 max-w-xs">
                  {activeProject 
                    ? 'Create activities in your schedule to see health metrics.'
                    : 'Select a project to view schedule health.'}
                </p>
                {!activeProject && projects.length > 0 && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-4"
                    onClick={() => navigate('/projects')}
                  >
                    Select Project
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Alerts & Notices Panel */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-slate-400" />
              Alerts & Notices
            </CardTitle>
          </CardHeader>
          <CardContent>
            {scheduleHealth && scheduleHealth.criticalCount > 0 ? (
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-100">
                  <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-700">Critical Path Activities</p>
                    <p className="text-xs text-amber-600 mt-0.5">
                      {scheduleHealth.criticalCount} activities on critical path with zero float
                    </p>
                  </div>
                </div>
                {scheduleHealth.notStartedCount > 0 && (
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
                    <Clock className="h-4 w-4 text-slate-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-slate-700">Pending Activities</p>
                      <p className="text-xs text-slate-600 mt-0.5">
                        {scheduleHealth.notStartedCount} activities not yet started
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mb-3">
                  <CheckCircle2 className="h-6 w-6 text-emerald-500" />
                </div>
                <p className="text-sm text-slate-600 mb-1">All clear</p>
                <p className="text-xs text-slate-400">
                  No critical alerts at this time
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Milestones */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-slate-400" />
              Upcoming Activities
            </CardTitle>
          </CardHeader>
          <CardContent>
            {upcomingMilestones.length > 0 ? (
              <div className="space-y-3">
                {upcomingMilestones.map((activity) => (
                  <div 
                    key={activity.id} 
                    className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100"
                  >
                    <div className={`h-2 w-2 rounded-full flex-shrink-0 ${
                      activity.is_critical ? 'bg-amber-500' : 'bg-blue-500'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{activity.name}</p>
                      <p className="text-xs text-slate-500">{activity.activity_id}</p>
                    </div>
                    <Badge variant="outline" className="text-[10px] flex-shrink-0">
                      {formatDate(activity.early_finish)}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <CalendarDays className="h-8 w-8 text-slate-300 mb-2" />
                <p className="text-xs text-slate-400">
                  No upcoming activities
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <Clock className="h-4 w-4 text-slate-400" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentActivities.length > 0 ? (
              <div className="space-y-3">
                {recentActivities.map((activity) => (
                  <div 
                    key={activity.id} 
                    className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100"
                  >
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      activity.status === 'completed' ? 'bg-emerald-100' :
                      activity.status === 'in_progress' ? 'bg-blue-100' : 'bg-slate-200'
                    }`}>
                      {activity.status === 'completed' ? (
                        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      ) : activity.status === 'in_progress' ? (
                        <Clock className="h-4 w-4 text-blue-600" />
                      ) : (
                        <CalendarDays className="h-4 w-4 text-slate-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{activity.name}</p>
                      <p className="text-xs text-slate-500">
                        {activity.status === 'completed' ? 'Completed' :
                         activity.status === 'in_progress' ? 'In Progress' : 'Not Started'}
                        {activity.total_float !== null && ` • ${activity.total_float}d float`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Clock className="h-8 w-8 text-slate-300 mb-2" />
                <p className="text-xs text-slate-400">
                  No recent activity
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Access - Projects */}
      {projects.length > 0 && !activeProject && (
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <Building2 className="h-4 w-4 text-slate-400" />
              Quick Access
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {projects.slice(0, 6).map((project) => (
                <div 
                  key={project.id}
                  className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-slate-300 cursor-pointer transition-colors"
                  onClick={() => handleProjectClick(project.id)}
                >
                  <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <Building2 className="h-5 w-5 text-slate-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{project.name}</p>
                    <p className="text-xs text-slate-400">{project.status}</p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-slate-400" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DashboardPage;
